import { useTranslation } from '../../i18n';
import React, { useState, useEffect, useRef } from 'react';
import {
  Select,
  Input,
  Switch,
  Form,
  Row,
  Col,
  Space,
  Collapse,
  Tag,
  Tooltip,
  Button,
  Radio,
  Segmented,
  Divider
} from 'antd';
import {
  QuestionCircleOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import TagsInput, { isQueryParam, isHeaderName, isOrigin } from '../TagsInput';
import { DurationInput, CountInput, SizeInput, RateLimitPresets, TTL_PRESETS } from './UnitInputs';
import { getWafRulesetOptions, SEVERITY_COLORS, SEVERITY_NAMES } from '../../constants/wafRules';
import CustomResponseEditor, { Folded, StatusInput, validateCustomResponse } from './CustomResponseEditor';
import { UpgradeTag } from './PlanGate';

const { Option } = Select;
const { Panel } = Collapse;

// 默认缓存配置(新子键缺省 = 跟随域名)
const DEFAULT_CACHE = {
  enabled: 'auto',
  ttl: 3600,
  ttl_mode: 'origin_or_default'
};

const DEFAULT_BROWSER_CACHE = {
  enabled: 'auto',
  ttl: 7200,
  ttl_mode: 'origin_or_default'
};

// 旧版写入但边缘从未执行的子键:加载时剥掉,不再保存
const LEGACY_CACHE_KEYS = ['cache_query_mode', 'cache_query_params', 'vary_by_device', 'vary_by_country', 'vary_by_language', 'cache_host_mode', 'stale_enabled'];
const LEGACY_OPT_KEYS = ['minify_html', 'minify_css', 'minify_js'];

const NAMED_KEY_TYPES = ['header', 'cookie', 'args'];
const keyTypeOf = (key) => String(key || 'ip').split(':')[0] || 'ip';
const keyNameOf = (rl) => {
  if (!rl) return '';
  if (rl.keyName !== undefined && rl.keyName !== null) return String(rl.keyName);
  const k = String(rl.key || '');
  return k.includes(':') ? k.slice(k.indexOf(':') + 1) : '';
};

const isProxyTarget = (v) => /^(https?:\/\/)?([a-z0-9_-]+(\.[a-z0-9_-]+)*|\[[0-9a-f:.]+\])(:\d{1,5})?\/?$/i.test(String(v).trim());

export const RATE_KEY_NAME_OK = (type, name) => {
  const n = String(name || '').trim();
  if (!n) return false;
  if (type === 'header') return isHeaderName(n);
  return !/\s/.test(n);
};

const PANEL_DEFAULTS = {
  cache: () => ({ ...DEFAULT_CACHE }),
  browser_cache: () => ({ ...DEFAULT_BROWSER_CACHE }),
  optimization: () => ({ gzip_enabled: true, brotli_enabled: false }),
  network: () => ({}),
  waf: () => ({ enabled: true, rules: [] }),
  ratelimit: () => ({ enabled: true, requests: 100, ttl: 60, key: 'ip' }),
  block: () => ({ status: 403 }),
  challenge: () => ({ kind: 'js' }),
  allow: () => ({}),
  verify_signature: () => ({ secret: '', sign_type: 'd', param: 'sign', ttl: 3600 }),
  cors: () => ({
    enabled: true,
    allow_origins: ['*'],
    allow_methods: ['GET', 'HEAD', 'POST', 'OPTIONS'],
    allow_headers: [],
    expose_headers: [],
    allow_credentials: false,
    max_age: 86400
  }),
  security_headers: () => ({ enabled: false }),
  proxy: () => ({ target: [], mode: '', hostname: '', protocol: 'auto' }),
  rewrite: () => ({ url: '' }),
  request_headers: () => ({}),
  ssl: () => ({ mode: 'flexible', always_https: false }),
  origin_shield: () => ({ max_concurrency: 0, action: ['queue', 'block'] }),
  headers: () => ({}),
  redirect: () => ({ url: '', code: 301 })
};

// ── 序列化 / 清理 ────────────────────────────────────────────────────────────

const isPlainObj = (v) => v && typeof v === 'object' && !Array.isArray(v);

const cleanCR = (cr) => {
  if (!isPlainObj(cr) || !String(cr.body || '').length) return undefined;
  const out = { body: cr.body };
  if (cr.status) out.status = Number(cr.status);
  if (cr.content_type && String(cr.content_type).trim()) out.content_type = String(cr.content_type).trim();
  return out;
};

// 去掉 ''/null/undefined/空数组/空对象(跟随域名);CR 无 body 视为未配置
const deepClean = (obj) => {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (k === 'response') {
      const cr = cleanCR(v);
      if (cr) out[k] = cr;
      return;
    }
    if (Array.isArray(v)) {
      const arr = v.filter((x) => x !== '' && x !== null && x !== undefined);
      if (arr.length) out[k] = arr;
      return;
    }
    if (isPlainObj(v)) {
      const c = deepClean(v);
      if (Object.keys(c).length) out[k] = c;
      return;
    }
    out[k] = v;
  });
  return out;
};

/** 把编辑态转换成提交给 API 的 action 包(幂等,可重复调用) */
export const serializeActions = (actions) => {
  const out = {};
  Object.entries(actions || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    // 用户自定义头原样保留
    if (k === 'headers' || k === 'request_headers') {
      out[k] = v;
      return;
    }
    if (k === 'proxy') {
      const target = (Array.isArray(v.target) ? v.target : (v.target ? [v.target] : []))
        .map((x) => String(x).trim()).filter(Boolean);
      const hostname = String(v.hostname || '').trim();
      if (target.length) {
        out.proxy = deepClean({ target, mode: v.mode, hostname, protocol: v.protocol || 'auto' });
      } else if (hostname) {
        out.set_host = { hostname };
      }
      return;
    }
    if (k === 'set_host') {
      if (!actions.proxy && v.hostname) out.set_host = { hostname: String(v.hostname).trim() };
      return;
    }
    if (k === 'ratelimit') {
      const rl = { ...v };
      const type = keyTypeOf(v.key);
      if (NAMED_KEY_TYPES.includes(type)) {
        const name = keyNameOf(v).trim();
        rl.key = `${type}:${name}`;
        rl.keyName = name;
      } else {
        rl.key = type;
        delete rl.keyName;
      }
      out[k] = deepClean(rl);
      return;
    }
    if (k === 'block') {
      const b = { status: v.status ? Number(v.status) : 403 };
      if (String(v.body || '').length) {
        b.body = v.body;
        if (v.content_type && String(v.content_type).trim()) b.content_type = String(v.content_type).trim();
      }
      out[k] = b;
      return;
    }
    if (k === 'origin_shield') {
      const os = { ...v };
      if (!(Array.isArray(os.action) && os.action.includes('waiting_room'))) delete os.waiting_room;
      out[k] = deepClean(os);
      return;
    }
    if (k === 'cache') {
      const c = { ...v };
      LEGACY_CACHE_KEYS.forEach((x) => delete c[x]);
      if (!['whitelist', 'blacklist'].includes(c.query_mode)) delete c.query_params;
      out[k] = deepClean(c);
      return;
    }
    if (k === 'optimization') {
      const o = { ...v };
      LEGACY_OPT_KEYS.forEach((x) => delete o[x]);
      if (!o.compress_level) delete o.compress_level;
      out[k] = deepClean(o);
      return;
    }
    if (k === 'network') {
      const n = { ...v };
      if (!n.max_body_size) delete n.max_body_size;
      out[k] = deepClean(n);
      return;
    }
    // 顶层键保留(如 allow: {});内部清理空值
    out[k] = isPlainObj(v) ? deepClean(v) : v;
  });
  return out;
};

/** 保存前校验,返回首个错误文案或 null */
export const validateActions = (actions, t) => {
  const a = actions || {};
  if (a.ratelimit) {
    const type = keyTypeOf(a.ratelimit.key);
    if (NAMED_KEY_TYPES.includes(type) && !RATE_KEY_NAME_OK(type, keyNameOf(a.ratelimit))) {
      return type === 'header' ? t('Rate limiting: enter a valid header name') : t('Rate limiting: enter a name without spaces');
    }
    const e = validateCustomResponse(a.ratelimit.response, t);
    if (e) return `${t('Rate limiting')}: ${e}`;
  }
  if (a.rewrite && !String(a.rewrite.url || '').startsWith('/')) return t('Rewrite path must start with /');
  if (a.proxy) {
    const bad = (a.proxy.target || []).find((x) => !isProxyTarget(x));
    if (bad) return t('Invalid origin: {{value}}', { value: bad });
  }
  if (a.verify_signature && !String(a.verify_signature.secret || '').trim()) return t('Signed URL: enter a secret key');
  if (a.cors) {
    const bad = (a.cors.allow_origins || []).find((x) => !isOrigin(x));
    if (bad) return t('Invalid origin: {{value}}', { value: bad });
  }
  const crs = [
    [t('Block'), a.block],
    [t('Origin shield'), a.origin_shield?.response],
    [t('Waiting room'), a.origin_shield?.waiting_room?.response]
  ];
  for (const [label, cr] of crs) {
    const e = validateCustomResponse(cr, t);
    if (e) return `${label}: ${e}`;
  }
  return null;
};

const initializeActions = (val) => {
  const r = { ...(isPlainObj(val) ? val : {}) };
  if (r.cache) {
    const c = { ...DEFAULT_CACHE, ...r.cache };
    LEGACY_CACHE_KEYS.forEach((k) => delete c[k]);
    r.cache = c;
  }
  if (r.browser_cache) r.browser_cache = { ...DEFAULT_BROWSER_CACHE, ...r.browser_cache };
  if (r.optimization) {
    const o = { ...r.optimization };
    LEGACY_OPT_KEYS.forEach((k) => delete o[k]);
    r.optimization = o;
  }
  if (r.proxy || r.set_host) {
    const p = isPlainObj(r.proxy) ? r.proxy : {};
    r.proxy = {
      ...PANEL_DEFAULTS.proxy(),
      ...p,
      target: Array.isArray(p.target) ? p.target : (p.target ? [p.target] : []),
      hostname: p.hostname || r.set_host?.hostname || ''
    };
    delete r.set_host;
  }
  if (r.ratelimit) r.ratelimit = { ...r.ratelimit, keyName: keyNameOf(r.ratelimit) };
  return r;
};

// ── 小部件 ───────────────────────────────────────────────────────────────────

const FOLLOW = '__follow__';
const TriState = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <Segmented
      size="small"
      value={value || FOLLOW}
      onChange={(v) => onChange(v === FOLLOW ? '' : v)}
      options={[
        { label: t('Follow domain'), value: FOLLOW },
        { label: t('On'), value: 'on' },
        { label: t('Off'), value: 'off' }
      ]}
    />
  );
};

const TTL_MODE_OPTIONS = (t) => [
  { value: 'origin_or_default', label: t('Follow origin or use default') },
  { value: 'fixed', label: t('Fixed') },
  { value: 'origin', label: t('Follow origin') },
  { value: 'min', label: t('Use minimum') },
  { value: 'max', label: t('Use maximum') }
];

const ttlLabel = (mode, t) => (mode === 'fixed' ? t('Cache TTL') : mode === 'origin' ? t('Fallback cache duration') : t('Default cache duration'));

const LADDER_OPTIONS = (t) => [
  { value: 'cache_first', label: t('Serve cache first') },
  { value: 'queue', label: t('Queue') },
  { value: 'challenge', label: t('Challenge') },
  { value: 'waiting_room', label: t('Waiting room') },
  { value: 'block', label: t('Block') }
];

/** 有序多选(按点选顺序执行),标签带序号 */
export const LadderSelect = ({ value = [], onChange }) => {
  const { t } = useTranslation();
  const opts = LADDER_OPTIONS(t);
  return (
    <Select
      mode="multiple"
      value={value}
      onChange={onChange}
      options={opts}
      placeholder={t('Select an action sequence')}
      style={{ width: '100%' }}
      tagRender={({ value: v, closable, onClose }) => (
        <Tag closable={closable} onClose={onClose} style={{ marginInlineEnd: 4 }}>
          {value.indexOf(v) + 1}. {opts.find((o) => o.value === v)?.label || v}
        </Tag>
      )}
    />
  );
};

/** 等候室 {pass_minutes, refresh_secs} */
export const WaitingRoomFields = ({ value = {}, onChange }) => {
  const { t } = useTranslation();
  const wr = value || {};
  return (
    <Row gutter={16}>
      <Col xs={24} md={12}>
        <Form.Item label={t('Pass valid for')} tooltip={t('How long a visitor who got through can browse without waiting again')}>
          <CountInput
            value={wr.pass_minutes}
            onChange={(v) => onChange({ ...wr, pass_minutes: v })}
            presets={[5, 10, 30, 60, 240]}
            format={(v) => t('{{n}} min', { n: v })}
            emptyLabel={t('Default (10 min)')}
            min={1}
            max={1440}
          />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label={t('Page refresh')} tooltip={t('How often the waiting page checks for a free slot')}>
          <DurationInput
            value={wr.refresh_secs}
            onChange={(v) => onChange({ ...wr, refresh_secs: v })}
            presets={[5, 10, 20, 30, 60]}
            emptyLabel={t('Default (10 s)')}
            min={3}
            max={120}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

// ── 主组件 ───────────────────────────────────────────────────────────────────

const GROUPS = [
  { title: 'Cache & delivery', keys: ['cache', 'browser_cache', 'optimization', 'network'] },
  { title: 'Security', keys: ['waf', 'ratelimit', 'block', 'challenge', 'allow', 'verify_signature', 'cors', 'security_headers'] },
  { title: 'Origin', keys: ['proxy', 'rewrite', 'request_headers', 'ssl', 'origin_shield'] },
  { title: 'Response', keys: ['headers', 'redirect'] }
];

/** 动作键 → 友好名(表格标签也用) */
export const ACTION_LABELS = {
  cache: 'Cache',
  browser_cache: 'Browser cache',
  optimization: 'Optimization',
  network: 'Network',
  waf: 'WAF',
  ratelimit: 'Rate limiting',
  block: 'Block',
  challenge: 'Challenge',
  allow: 'Allow',
  verify_signature: 'Signed URL',
  cors: 'CORS',
  security_headers: 'Security headers',
  proxy: 'Origin override',
  set_host: 'Origin override',
  rewrite: 'Rewrite',
  request_headers: 'Origin request headers',
  ssl: 'SSL/TLS',
  origin_shield: 'Origin shield',
  headers: 'Custom headers',
  redirect: 'Redirect'
};

const PANEL_COLORS = {
  cache: 'blue', browser_cache: 'cyan', optimization: 'gold', network: 'lime',
  waf: 'red', ratelimit: 'orange', block: 'volcano', challenge: 'magenta', allow: 'green',
  verify_signature: 'purple', cors: 'geekblue', security_headers: 'red',
  proxy: 'blue', rewrite: 'cyan', request_headers: 'geekblue', ssl: 'green', origin_shield: 'orange',
  headers: 'purple', redirect: 'geekblue'
};

const PANEL_TIPS = {
  allow: 'Skip WAF and later firewall blocks',
  rewrite: 'Change the path sent to the origin; the visitor URL stays the same',
  security_headers: 'Send domain security headers',
  request_headers: 'Request headers sent to the origin (e.g., Origin); leave a value blank to remove that header',
  ssl: 'These settings control connections from CDN nodes to the origin and do not affect client-to-CDN connections'
};

// locks(用户端按套餐缓存级别传入):{ cache: 'advanced'|null, forceCache: 'full'|null } —— 非空 = 套餐不含,显示升级标签并禁用
const ActionBuilder = ({ value = {}, onChange, locks = {} }) => {
  const { t } = useTranslation();
  const [actions, setActions] = useState(() => initializeActions(value));
  const [open, setOpen] = useState(() => Object.keys(initializeActions(value)));
  const prevValueRef = useRef(JSON.stringify(value));

  // 外部 value 变化(加载规则)时重建编辑态;自己回传的值不触发
  useEffect(() => {
    const valueStr = JSON.stringify(value);
    if (valueStr !== prevValueRef.current) {
      prevValueRef.current = valueStr;
      const init = initializeActions(value);
      setActions(init);
      setOpen(Object.keys(init));
    }
  }, [value]);

  useEffect(() => {
    const out = serializeActions(actions);
    prevValueRef.current = JSON.stringify(out);
    onChange(out);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions]);

  const setAction = (key, data) => setActions((prev) => ({ ...prev, [key]: data }));
  const upd = (key, patch) => setActions((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  const removeAction = (key) => setActions((prev) => {
    const next = { ...prev };
    delete next[key];
    return next;
  });
  const enableAction = (key) => {
    setAction(key, PANEL_DEFAULTS[key]());
    setOpen((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  const header = (key) => (
    <Space size={6} wrap>
      <Tag color={PANEL_COLORS[key]} style={{ margin: 0 }}>{t(ACTION_LABELS[key])}</Tag>
      {PANEL_TIPS[key] && (
        <Tooltip title={t(PANEL_TIPS[key])}>
          <QuestionCircleOutlined style={{ color: '#999' }} />
        </Tooltip>
      )}
      {!actions[key] && <span style={{ color: '#999', fontSize: 12 }}>{t('Not set')}</span>}
      {key === 'cache' && locks.cache && <UpgradeTag need={locks.cache} />}
    </Space>
  );

  const extra = (key) => (actions[key] ? (
    <Button type="text" danger size="small" onClick={(e) => { e.stopPropagation(); removeAction(key); }}>
      {t('Remove')}
    </Button>
  ) : (
    <Button type="link" size="small" disabled={key === 'cache' && !!locks.cache} onClick={(e) => { e.stopPropagation(); enableAction(key); }}>
      {t('Enable')}
    </Button>
  ));

  // ── 各面板内容 ──

  const renderCache = () => {
    const c = actions.cache;
    return (
      <>
        <Form.Item label={t('Cache mode')}>
          <Radio.Group
            optionType="button"
            value={c.enabled ?? 'auto'}
            onChange={(e) => upd('cache', { enabled: e.target.value })}
            options={[
              { value: 'auto', label: t('Automatic') },
              { value: 'on', label: <span>{t('Force caching')}{locks.forceCache && c.enabled !== 'on' && <UpgradeTag need={locks.forceCache} />}</span>, disabled: !!locks.forceCache && c.enabled !== 'on' },
              { value: 'off', label: t('Do not cache content') }
            ]}
          />
        </Form.Item>
        {c.enabled !== 'off' && (
          <>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label={t('Cache TTL mode')}>
                  <Select value={c.ttl_mode ?? 'origin_or_default'} onChange={(v) => upd('cache', { ttl_mode: v })} options={TTL_MODE_OPTIONS(t)} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={ttlLabel(c.ttl_mode, t)}>
                  <DurationInput value={c.ttl ?? 3600} onChange={(v) => upd('cache', { ttl: v })} presets={TTL_PRESETS} zeroLabel={t('No cache')} min={0} max={31536000} />
                </Form.Item>
              </Col>
            </Row>
            <Folded label={t('Advanced')}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label={t('Query string handling')}>
                    <Select
                      value={c.query_mode || ''}
                      onChange={(v) => upd('cache', { query_mode: v })}
                      options={[
                        { value: '', label: t('Follow domain') },
                        { value: 'ignore', label: t('Ignore') },
                        { value: 'sorted', label: t('All parameters (sorted)') },
                        { value: 'whitelist', label: t('Allowlist') },
                        { value: 'blacklist', label: t('Blocklist') }
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t('Cache host mode')}>
                    <Select
                      value={c.host_mode || ''}
                      onChange={(v) => upd('cache', { host_mode: v })}
                      options={[
                        { value: '', label: t('Follow domain') },
                        { value: 'original', label: t('Visitor hostname') },
                        { value: 'host_header', label: t('Origin Host (shared cache)') }
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {['whitelist', 'blacklist'].includes(c.query_mode) && (
                <Form.Item label={c.query_mode === 'whitelist' ? t('Query parameter allowlist') : t('Query parameter blocklist')}>
                  <TagsInput
                    format="array"
                    value={Array.isArray(c.query_params) ? c.query_params : []}
                    onChange={(v) => upd('cache', { query_params: v })}
                    validate={(v) => (isQueryParam(v) ? null : t('Invalid parameter name'))}
                    placeholder={c.query_mode === 'whitelist' ? t('Enter the parameters to keep') : t('Enter the parameters to exclude')}
                  />
                </Form.Item>
              )}
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label={t('Extra variants')} tooltip={t('Cache a separate copy per device type, country or language')}>
                    <Select
                      mode="multiple"
                      value={Array.isArray(c.vary) ? c.vary : []}
                      onChange={(v) => upd('cache', { vary: v })}
                      placeholder={t('Follow domain')}
                      options={[
                        { value: 'device', label: t('Device type') },
                        { value: 'country', label: t('Country') },
                        { value: 'language', label: t('Language') }
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t('Serve stale cache')} tooltip={t('Serve expired cache when the origin fails')}>
                    <TriState value={c.stale} onChange={(v) => upd('cache', { stale: v })} />
                  </Form.Item>
                </Col>
              </Row>
            </Folded>
          </>
        )}
      </>
    );
  };

  const renderBrowserCache = () => {
    const b = actions.browser_cache;
    return (
      <>
        <Form.Item label={t('Cache mode')}>
          <Radio.Group
            optionType="button"
            value={b.enabled ?? 'auto'}
            onChange={(e) => upd('browser_cache', { enabled: e.target.value })}
            options={[
              { value: 'auto', label: t('Automatic') },
              { value: 'on', label: t('Force browser caching') },
              { value: 'off', label: t('Disable browser caching') }
            ]}
          />
        </Form.Item>
        {b.enabled !== 'off' && (
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label={t('Cache TTL mode')}>
                <Select value={b.ttl_mode ?? 'origin_or_default'} onChange={(v) => upd('browser_cache', { ttl_mode: v })} options={TTL_MODE_OPTIONS(t)} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={ttlLabel(b.ttl_mode, t)}>
                <DurationInput value={b.ttl ?? 7200} onChange={(v) => upd('browser_cache', { ttl: v })} presets={TTL_PRESETS} zeroLabel={t('No cache')} min={0} max={31536000} />
              </Form.Item>
            </Col>
          </Row>
        )}
      </>
    );
  };

  const renderOptimization = () => {
    const o = actions.optimization;
    const m = o.minify || {};
    return (
      <>
        <Row gutter={16}>
          <Col xs={12} md={6}>
            <Form.Item label={t('Gzip compression')}>
              <Switch checked={!!o.gzip_enabled} onChange={(v) => upd('optimization', { gzip_enabled: v })} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label={t('Brotli compression')}>
              <Switch checked={!!o.brotli_enabled} onChange={(v) => upd('optimization', { brotli_enabled: v })} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label={t('Compression')}>
              <TriState value={o.compress} onChange={(v) => upd('optimization', { compress: v })} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          {[['html', 'Minify HTML'], ['css', 'Minify CSS'], ['js', 'Minify JavaScript']].map(([k, label]) => (
            <Col xs={24} md={8} key={k}>
              <Form.Item label={t(label)}>
                <TriState value={m[k]} onChange={(v) => upd('optimization', { minify: { ...m, [k]: v } })} />
              </Form.Item>
            </Col>
          ))}
        </Row>
        <Folded label={t('Advanced')}>
          <Form.Item label={t('Compression level')}>
            <Select
              value={Number(o.compress_level) || 0}
              onChange={(v) => upd('optimization', { compress_level: v })}
              options={[{ value: 0, label: t('Default') }, ...[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => ({ value: n, label: String(n) }))]}
              style={{ maxWidth: 200 }}
            />
          </Form.Item>
        </Folded>
      </>
    );
  };

  const renderNetwork = () => {
    const n = actions.network;
    return (
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item label={t('Origin timeout')}>
            <DurationInput
              value={n.proxy_timeout ?? null}
              onChange={(v) => upd('network', { proxy_timeout: v })}
              presets={[10, 30, 60, 120, 300, 600]}
              emptyLabel={t('Follow domain')}
              min={1}
              max={3600}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label={t('Max request body')}>
            <SizeInput
              base="B"
              value={n.max_body_size ?? 0}
              onChange={(v) => upd('network', { max_body_size: v ?? 0 })}
              presets={[1048576, 10485760, 104857600, 536870912, 1073741824]}
              zeroLabel={t('Follow domain')}
              min={0}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="WebSocket">
            <TriState value={n.websocket} onChange={(v) => upd('network', { websocket: v })} />
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderWaf = () => {
    const w = actions.waf;
    return (
      <>
        <Form.Item label={t('WAF')} tooltip={t('Turning it on only works when the domain WAF is on')}>
          <Switch
            checked={w.enabled !== false}
            onChange={(v) => upd('waf', { enabled: v })}
            checkedChildren={t('On')}
            unCheckedChildren={t('Off')}
          />
        </Form.Item>
        {w.enabled !== false && (
          <Form.Item label={t('Only run these rulesets')}>
            <Select
              mode="multiple"
              value={w.rules || []}
              onChange={(v) => upd('waf', { rules: v })}
              placeholder={t('All domain rulesets')}
              style={{ width: '100%' }}
              optionLabelProp="label"
            >
              {getWafRulesetOptions().map((option) => (
                <Option key={option.value} value={option.value} label={t(option.label)}>
                  <Space>
                    {t(option.label)}
                    <Tag color={SEVERITY_COLORS[option.severity]} style={{ margin: 0 }}>
                      {t(SEVERITY_NAMES[option.severity])}
                    </Tag>
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </>
    );
  };

  const renderRateLimit = () => {
    const rl = actions.ratelimit;
    const type = keyTypeOf(rl.key);
    const name = keyNameOf(rl);
    const named = NAMED_KEY_TYPES.includes(type);
    const nameOk = !named || !name || RATE_KEY_NAME_OK(type, name);
    return (
      <>
        <div style={{ marginBottom: 12 }}>
          <RateLimitPresets
            requests={rl.requests || 100}
            ttl={rl.ttl || 60}
            onPick={(p) => upd('ratelimit', p)}
          />
        </div>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item label={t('Max requests')}>
              <CountInput value={rl.requests || 100} onChange={(v) => upd('ratelimit', { requests: v })} presets={[10, 30, 60, 100, 300, 1000]} min={1} max={10000} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label={t('Per')}>
              <DurationInput value={rl.ttl || 60} onChange={(v) => upd('ratelimit', { ttl: v })} presets={[1, 10, 60, 300, 3600]} min={1} max={3600} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label={t('Rate limit key type')}>
              <Select
                value={type}
                onChange={(v) => upd('ratelimit', NAMED_KEY_TYPES.includes(v) ? { key: `${v}:`, keyName: '' } : { key: v, keyName: undefined })}
              >
                <Option value="ip">{t('IP address')}</Option>
                <Option value="country">{t('Country')}</Option>
                <Option value="uri">{t('URI path')}</Option>
                <Option value="header">{t('Request headers')}</Option>
                <Option value="cookie">Cookie</Option>
                <Option value="args">{t('Query parameters')}</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {named && (
          <Form.Item
            label={type === 'cookie' ? t('Cookie name') : type === 'header' ? t('Header name') : t('Parameter name')}
            required
            validateStatus={nameOk ? undefined : 'error'}
            help={nameOk ? undefined : (type === 'header' ? t('Invalid header name') : t('Name cannot contain spaces'))}
          >
            <Input
              value={name}
              onChange={(e) => upd('ratelimit', { key: `${type}:${e.target.value}`, keyName: e.target.value })}
              placeholder={type === 'cookie' ? 'session_id' : type === 'header' ? 'X-API-Key' : 'user_id'}
            />
          </Form.Item>
        )}
        <Form.Item label={t('Status')}>
          <Switch
            checked={rl.enabled !== false}
            onChange={(v) => upd('ratelimit', { enabled: v })}
            checkedChildren={t('On')}
            unCheckedChildren={t('Off')}
          />
        </Form.Item>
        <Folded label={t('Custom response')}>
          <CustomResponseEditor
            value={rl.response}
            onChange={(v) => upd('ratelimit', { response: v })}
            defaultStatus={429}
            statusPresets={[429, 503]}
          />
        </Folded>
      </>
    );
  };

  const renderBlock = () => {
    const b = actions.block;
    return (
      <>
        <Form.Item label={t('Status code')}>
          <StatusInput value={b.status} onChange={(v) => upd('block', { status: v })} presets={[403, 404, 410, 429, 451, 503]} placeholder="403" />
        </Form.Item>
        <Folded label={t('Custom response')}>
          <CustomResponseEditor
            value={b}
            onChange={(v) => upd('block', { content_type: v.content_type, body: v.body })}
            showStatus={false}
          />
        </Folded>
      </>
    );
  };

  const renderChallenge = () => (
    <Form.Item label={t('Challenge type')}>
      <Radio.Group
        optionType="button"
        value={actions.challenge.kind || 'js'}
        onChange={(e) => upd('challenge', { kind: e.target.value })}
        options={[
          { value: 'js', label: t('JS challenge') },
          { value: 'captcha', label: t('CAPTCHA') }
        ]}
      />
    </Form.Item>
  );

  const renderVerifySignature = () => {
    const v = actions.verify_signature;
    return (
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item label={t('Secret key')} required>
            <Input.Password value={v.secret || ''} onChange={(e) => upd('verify_signature', { secret: e.target.value })} autoComplete="new-password" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t('Signature type')} tooltip="A: ?auth_key=ts-rand-uid-md5 · B: /ts/md5/file · C: /md5/hexts/file · D: ?sign=hmac&t=deadline">
            <Select
              value={v.sign_type || 'd'}
              onChange={(x) => upd('verify_signature', { sign_type: x })}
              options={[
                { value: 'a', label: t('Type {{n}}', { n: 'A' }) + ' (MD5)' },
                { value: 'b', label: t('Type {{n}}', { n: 'B' }) + ' (MD5)' },
                { value: 'c', label: t('Type {{n}}', { n: 'C' }) + ' (MD5)' },
                { value: 'd', label: t('Type {{n}}', { n: 'D' }) + ' (HMAC-SHA256)' }
              ]}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t('Parameter name')}>
            <Input value={v.param ?? ''} onChange={(e) => upd('verify_signature', { param: e.target.value })} placeholder="sign" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t('Valid for')}>
            <DurationInput value={v.ttl ?? 3600} onChange={(x) => upd('verify_signature', { ttl: x })} presets={[300, 1800, 3600, 86400]} min={1} max={31536000} />
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderCors = () => {
    const c = actions.cors;
    return (
      <>
        <Form.Item label="CORS" tooltip={t('Replaces the domain CORS policy for matching URLs')}>
          <Switch checked={c.enabled !== false} onChange={(v) => upd('cors', { enabled: v })} checkedChildren={t('On')} unCheckedChildren={t('Off')} />
        </Form.Item>
        {c.enabled !== false && (
          <>
            <Form.Item label={t('Allowed origins')}>
              <TagsInput
                format="array"
                value={c.allow_origins || []}
                onChange={(v) => upd('cors', { allow_origins: v })}
                validate={(v) => (isOrigin(v) ? null : t('Use * or http(s)://host[:port]'))}
                placeholder="https://app.example.com"
              />
            </Form.Item>
            <Folded label={t('Advanced')}>
              <Form.Item label={t('Allowed methods')}>
                <Select
                  mode="multiple"
                  value={c.allow_methods || []}
                  onChange={(v) => upd('cors', { allow_methods: v })}
                  options={['GET', 'HEAD', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'].map((m) => ({ value: m, label: m }))}
                />
              </Form.Item>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label={t('Allowed headers')}>
                    <TagsInput format="array" value={c.allow_headers || []} onChange={(v) => upd('cors', { allow_headers: v })} validate={(v) => (v === '*' || isHeaderName(v) ? null : t('Invalid header name'))} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t('Exposed headers')}>
                    <TagsInput format="array" value={c.expose_headers || []} onChange={(v) => upd('cors', { expose_headers: v })} validate={(v) => (isHeaderName(v) ? null : t('Invalid header name'))} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t('Allow credentials')}>
                    <Switch checked={!!c.allow_credentials} onChange={(v) => upd('cors', { allow_credentials: v })} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t('Preflight cache')}>
                    <DurationInput value={c.max_age ?? 86400} onChange={(v) => upd('cors', { max_age: v })} presets={[600, 3600, 86400, 604800]} zeroLabel={t('No cache')} min={0} max={31536000} />
                  </Form.Item>
                </Col>
              </Row>
            </Folded>
          </>
        )}
      </>
    );
  };

  const renderSecurityHeaders = () => (
    <Form.Item label={t('Send domain security headers')}>
      <Switch checked={!!actions.security_headers.enabled} onChange={(v) => upd('security_headers', { enabled: v })} />
    </Form.Item>
  );

  const renderProxy = () => {
    const p = actions.proxy;
    return (
      <>
        <Form.Item label={t('Origins')} tooltip={t('Leave empty to only change the Host header')}>
          <TagsInput
            format="array"
            value={p.target || []}
            onChange={(v) => upd('proxy', { target: v })}
            validate={(v) => (isProxyTarget(v) ? null : t('Use host[:port] or http(s)://host[:port]'))}
            placeholder="203.0.113.5:443, https://origin.example.com"
          />
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item label={t('Host header')}>
              <Input value={p.hostname || ''} onChange={(e) => upd('proxy', { hostname: e.target.value })} placeholder="origin.example.com" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label={t('Origin protocol')}>
              <Select
                value={p.protocol || 'auto'}
                onChange={(v) => upd('proxy', { protocol: v })}
                options={[
                  { value: 'auto', label: t('Automatic') },
                  { value: 'http', label: 'HTTP' },
                  { value: 'https', label: 'HTTPS' }
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label={t('Load balancing')}>
              <Select
                value={p.mode || ''}
                onChange={(v) => upd('proxy', { mode: v })}
                options={[
                  { value: '', label: t('Default') },
                  { value: 'round_robin', label: t('Round robin') },
                  { value: 'ip_hash', label: t('IP hash') },
                  { value: 'first', label: t('First available') }
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      </>
    );
  };

  const renderRewrite = () => {
    const url = actions.rewrite.url || '';
    const ok = !url || url.startsWith('/');
    return (
      <Form.Item
        label={t('New path')}
        required
        validateStatus={ok ? undefined : 'error'}
        help={ok ? undefined : t('Rewrite path must start with /')}
      >
        <Input value={url} onChange={(e) => upd('rewrite', { url: e.target.value })} placeholder="/new/path?x=1" />
      </Form.Item>
    );
  };

  const renderOriginShield = () => {
    const os = actions.origin_shield;
    const ladder = Array.isArray(os.action) ? os.action : [];
    const wr = os.waiting_room || {};
    return (
      <>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label={t('Max concurrent origin requests')} tooltip={t('0 = use the domain limit')}>
              <CountInput value={os.max_concurrency ?? 0} onChange={(v) => upd('origin_shield', { max_concurrency: v ?? 0 })} presets={[10, 20, 50, 100, 500]} zeroLabel={t('Domain limit')} min={0} max={1000000} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label={t('Over-limit actions (sequence)')}>
              <LadderSelect value={ladder} onChange={(v) => upd('origin_shield', { action: v })} />
            </Form.Item>
          </Col>
        </Row>
        <Folded label={t('Advanced')}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label={t('Maximum queue length')}>
                <CountInput value={os.queue_max ?? 0} onChange={(v) => upd('origin_shield', { queue_max: v ?? 0 })} presets={[50, 200, 500, 1000, 5000]} zeroLabel={t('Default (100)')} min={0} max={1000000} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={t('Queue wait')}>
                <DurationInput base="ms" value={os.queue_wait_ms ?? 0} onChange={(v) => upd('origin_shield', { queue_wait_ms: v ?? 0 })} presets={[1000, 3000, 5000, 10000, 30000]} zeroLabel={t('Default (500 ms)')} min={0} max={600000} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={t('Retry-After')}>
                <DurationInput value={os.retry_after ?? 0} onChange={(v) => upd('origin_shield', { retry_after: v ?? 0 })} presets={[5, 10, 30, 60, 300]} zeroLabel={t('Default (1 s)')} min={0} max={86400} />
              </Form.Item>
            </Col>
          </Row>
        </Folded>
        <Folded label={t('Custom response')}>
          <CustomResponseEditor value={os.response} onChange={(v) => upd('origin_shield', { response: v })} defaultStatus={503} statusPresets={[503, 429]} />
        </Folded>
        {ladder.includes('waiting_room') && (
          <Folded label={t('Waiting room')}>
            <WaitingRoomFields value={wr} onChange={(v) => upd('origin_shield', { waiting_room: { ...wr, ...v } })} />
            <Divider plain style={{ fontSize: 12, margin: '4px 0 12px' }}>{t('Waiting page')}</Divider>
            <CustomResponseEditor
              value={wr.response}
              onChange={(v) => upd('origin_shield', { waiting_room: { ...wr, response: v } })}
              defaultStatus={503}
              waitingRoom
            />
          </Folded>
        )}
      </>
    );
  };

  const renderSsl = () => (
    <Row gutter={16}>
      <Col xs={24} md={12}>
        <Form.Item
          label={t('Origin SSL mode')}
          tooltip={t('Off: HTTP · Flexible: HTTPS, no validation · Full: HTTPS, self-signed OK · Strict: CA-issued only')}
        >
          <Select
            value={actions.ssl.mode || 'flexible'}
            onChange={(v) => upd('ssl', { mode: v })}
            options={[
              { value: 'off', label: t('Off') },
              { value: 'flexible', label: t('Flexible') },
              { value: 'full', label: t('Full') },
              { value: 'strict', label: t('Strict') }
            ]}
          />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label={t('Force HTTPS (client)')}>
          <Switch checked={!!actions.ssl.always_https} onChange={(v) => upd('ssl', { always_https: v })} />
        </Form.Item>
      </Col>
    </Row>
  );

  const renderRedirect = () => (
    <Row gutter={16}>
      <Col xs={24} md={16}>
        <Form.Item label={t('Target URL')}>
          <Input value={actions.redirect.url || ''} onChange={(e) => upd('redirect', { url: e.target.value })} placeholder="https://example.com/new-path" />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label={t('Status code')}>
          <Select value={actions.redirect.code || 301} onChange={(v) => upd('redirect', { code: v })}>
            <Option value={301}>{t('301 Permanent')}</Option>
            <Option value={302}>{t('302 Temporary')}</Option>
            <Option value={307}>{t('307 Temporary')}</Option>
            <Option value={308}>{t('308 Permanent')}</Option>
          </Select>
        </Form.Item>
      </Col>
    </Row>
  );

  const RENDER = {
    cache: renderCache,
    browser_cache: renderBrowserCache,
    optimization: renderOptimization,
    network: renderNetwork,
    waf: renderWaf,
    ratelimit: renderRateLimit,
    block: renderBlock,
    challenge: renderChallenge,
    allow: () => null,
    verify_signature: renderVerifySignature,
    cors: renderCors,
    security_headers: renderSecurityHeaders,
    proxy: renderProxy,
    rewrite: renderRewrite,
    request_headers: () => <HeadersEditor value={actions.request_headers} onChange={(v) => setAction('request_headers', v)} />,
    ssl: renderSsl,
    origin_shield: renderOriginShield,
    headers: () => <HeadersEditor value={actions.headers} onChange={(v) => setAction('headers', v)} />,
    redirect: renderRedirect
  };

  return (
    <>
      {GROUPS.map((g) => (
        <div key={g.title} style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#888', margin: '8px 0 4px' }}>{t(g.title)}</div>
          <Collapse
            size="small"
            activeKey={open.filter((k) => g.keys.includes(k))}
            onChange={(keys) => {
              const arr = Array.isArray(keys) ? keys : [keys];
              setOpen((prev) => [...prev.filter((k) => !g.keys.includes(k)), ...arr]);
            }}
          >
            {g.keys.map((key) => (
              <Panel
                key={key}
                header={header(key)}
                extra={extra(key)}
                collapsible={key === 'allow' ? 'icon' : undefined}
                showArrow={key !== 'allow'}
              >
                {actions[key] ? RENDER[key]() : null}
              </Panel>
            ))}
          </Collapse>
        </div>
      ))}
    </>
  );
};

// Helper component for headers editing
const HeadersEditor = ({ value = {}, onChange }) => {
  const { t } = useTranslation();
  const [headers, setHeaders] = useState([]);

  useEffect(() => {
    const headerArray = Object.entries(value || {}).map(([key, val], index) => ({
      id: index,
      key,
      value: val
    }));
    setHeaders(headerArray.length > 0 ? headerArray : [{ id: 0, key: '', value: '' }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const headerObj = {};
    headers.forEach((h) => {
      if (h.key && h.value) {
        headerObj[h.key] = h.value;
      }
    });
    onChange(headerObj);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers]);

  const addHeader = () => setHeaders([...headers, { id: Date.now(), key: '', value: '' }]);
  const updateHeader = (id, field, val) => setHeaders(headers.map((h) => (h.id === id ? { ...h, [field]: val } : h)));
  const removeHeader = (id) => {
    if (headers.length > 1) setHeaders(headers.filter((h) => h.id !== id));
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {headers.map((header) => (
        <Row key={header.id} gutter={8} wrap={false}>
          <Col flex="2">
            <Input value={header.key} onChange={(e) => updateHeader(header.id, 'key', e.target.value)} placeholder={t('Header name')} />
          </Col>
          <Col flex="3">
            <Input value={header.value} onChange={(e) => updateHeader(header.id, 'value', e.target.value)} placeholder={t('Header value')} />
          </Col>
          <Col flex="none">
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeHeader(header.id)} />
          </Col>
        </Row>
      ))}
      <Button type="dashed" onClick={addHeader} block icon={<PlusOutlined />}>
        {t('Add header')}
      </Button>
    </Space>
  );
};

export default ActionBuilder;
